import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MenuItem } from '@mui/material';
import { TextField } from 'formik-mui';
import { Field, FormikProvider, useFormik } from 'formik';
import { FilterFormValues } from '@sr/dto';
import { TextFieldClearable } from 'shared/ui/form-fields/text-field-clearable';
import { formValuesFromUrlSearchParams, mergeFormValuesVsCurrentSearchParams } from 'utils/url-search-params.helper';
import { PAGE_FIELD_NAME } from 'shared/hooks/paging-params';
import { FilterWrapper, ValueFormatterContext } from 'shared/ui/filter/filter-wrapper';

type UserFilter = {
  userName: string;
  isActiveOnly: '' | 'true' | 'false';
  includeFired: '' | 'true' | 'false';
};

export const defaultFormValues: UserFilter = {
  userName: '',
  includeFired: '',
  isActiveOnly: '',
};

export type FormValues = FilterFormValues<UserFilter>;

const DEBOUNCE_DELAY = 500;

export const UsersListFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const employmentFormatter = useCallback(
    ({ value }: ValueFormatterContext) => (value === 'true' ? 'Уволен' : 'Работает'),
    [],
  );
  const activityFormatter = useCallback(
    ({ value }: ValueFormatterContext) => (value === 'true' ? 'Активен' : 'Неактивен'),
    [],
  );
  const initialValues = useMemo(() => formValuesFromUrlSearchParams(searchParams, defaultFormValues), [searchParams]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: () => {},
    validateOnChange: true,
    initialTouched: { userName: true },
    validate: (values) => {
      if (!!values.userName && values.userName.length > 0 && values.userName.length < 3) {
        return { userName: 'Наберите хотя бы 3 символа для поиска' };
      }
    },
  });

  useEffect(() => {
    if (!formik.dirty || !formik.isValid) return;

    const cancel = setTimeout(() => {
      const params = mergeFormValuesVsCurrentSearchParams(searchParams, formik.values, defaultFormValues);
      // раз пользователь обновил фильтр, сбрасываем страницу на 0
      params.set(PAGE_FIELD_NAME, '0');
      if (params.toString() !== searchParams.toString()) setSearchParams(params, { replace: true });
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(cancel);
  }, [formik.values, formik.isValid, formik.dirty, searchParams, setSearchParams]);

  return (
    <FormikProvider value={formik}>
      <FilterWrapper title="Фильтры пользователей">
        <FilterWrapper.Item label="Фамилия/Имя" queryKeys="userName">
          <Field
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            type="text"
            placeholder="Фамилия/Имя"
            name="userName"
            label="Фамилия/Имя"
            helperText="Начните набирать для фильтрации"
            component={TextFieldClearable}
          />
        </FilterWrapper.Item>

        <FilterWrapper.Item label="Статус работника" queryKeys="includeFired" valueFormatter={employmentFormatter}>
          <Field label="Статус работника" fullWidth component={TextField} select name="includeFired">
            <MenuItem value="">&nbsp;</MenuItem>
            <MenuItem key={0} value={'false'}>
              Работает
            </MenuItem>
            <MenuItem key={1} value={'true'}>
              Уволен
            </MenuItem>
          </Field>
        </FilterWrapper.Item>

        <FilterWrapper.Item label="Активность" queryKeys="isActiveOnly" valueFormatter={activityFormatter}>
          <Field label="Активность" fullWidth component={TextField} select name="isActiveOnly">
            <MenuItem value="">&nbsp;</MenuItem>
            <MenuItem key={0} value={'false'}>
              Неактивен
            </MenuItem>
            <MenuItem key={1} value={'true'}>
              Активен
            </MenuItem>
          </Field>
        </FilterWrapper.Item>
      </FilterWrapper>
    </FormikProvider>
  );
};
